import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Radio, Typography} from "@mui/material";
import React, { useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { defaultObject } from "../../../../Helpers/typescriptHelper";
import { EEScorecard } from "../../../../Component/Model/Dto/EmploymentEquity/EEScorecard";
import { EEScorecardService } from "../../../../Component/Api/EmploymentEquity/Scorecard/EEScorecardService";
import { SkeletonLoading } from "../../../../Component/Common/SkeletonLoading/SkeletonLoading";
import { MDTypography } from "../../../../Component/Common/Components/MDTypography";
import { ExportKeyValue, FileExportRequest } from "../../../../Component/Model/Dto/Upload";
import { DataImportType } from "../../../../Component/Model/Enum/BEE123Enums";
import { ImportService } from "../../../../Component/Api/Import/ImportService";
import AppAccordion from "../../../../Component/Common/Components/Accordion/AppAccordion";
import {useHistory} from "react-router-dom";
import { toast } from "react-toastify";

interface IConsolidationResultsProps {
  consolidationId: number;
  scorecardId: string;
  showConsolidationResultsModal: boolean;
  closeConsolidationResultsModal: () => void;
}

export const ConsolidationResultsModal = (props: IConsolidationResultsProps) => {
  const history = useHistory();
  
  const [showIndividualConsolidationResults, setShowIndividualConsolidationResults] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(5);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setIsLoading] = useState<boolean>(true);
  const [conflictData, setConflictData] = useState<EEScorecard.IndividualConsolidationDto[]>(defaultObject);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [canGoToPreviousPage, setCanGoToPreviousPage] = useState<boolean>(true);
  const [canGoToNextPage, setCanGoToNextPage] = useState<boolean>(true);
  const [consolidationIndividualsMap, setConsolidationIndividualsMap] = useState<EEScorecard.ConsolidationIndividualsMap>({});
  const [showValidationFooterMessage, setShowValidationFooterMessage] = useState<boolean>(true);
  const [totalIndividualConflicts, setTotalIndividualConflicts] = useState<number>(0);

  useEffect(() => {
    GetConflictData();
  }, [pageSize, pageNumber]);

  const GetConflictData = async () => {
    setIsLoading(true);
    try {
      const result = await EEScorecardService.GetConflicts({
        consolidationId: props.consolidationId,
        pageNumber: pageNumber,
        pageSize: pageSize,
      });
      setConflictData(result.data.individualConsolidations);
      setShowIndividualConsolidationResults(
        result.data.individualConsolidations.length > 0
      );
      const individualsWithUnselectedEmployeeData =
        result.data.individualConsolidations.filter(
          (individual: EEScorecard.IndividualConsolidationDto) => {
            return individual.SelectedIndividualEmployeeDataId === 0;
          }
        );
      const totalPagesCalculation = Math.ceil(
        result.data.totalCount / pageSize
      );
      setTotalPages(totalPagesCalculation);
      setCanGoToPreviousPage(pageNumber > 1);
      setCanGoToNextPage(pageNumber < totalPagesCalculation);
      setTotalIndividualConflicts(result.data.totalCount);
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const GoToPreviousPage = () => {
    if (canGoToPreviousPage) {
      setPageNumber((prevPage: number) => prevPage - 1);
    }
  };

  const GoToNextPage = () => {
    if (canGoToNextPage) {
      setPageNumber((prevPage: number) => prevPage + 1);
    }
  };

  const NumberOfIndividualsText = (): string => {
    if (totalIndividualConflicts > 0) {
      const numberText =
        totalIndividualConflicts > 1
          ? totalIndividualConflicts.toString() + " people have"
          : "person has";
      const text =
        "The following " + numberText + " overlapping employee data records:";
      return text;
    }
    return "";
  };

  const handleRadioChange = (
    individual: EEScorecard.IndividualConsolidationDto,
    employmentData: EEScorecard.IndividualEmploymentRecordConsolidationDto
  ) => {
    let filteredIndividualEmploymentRecords : EEScorecard.IndividualEmploymentRecordConsolidationDto[] = individual.individualEmploymentRecords
      .filter(x =>x.sourceScorecardName === employmentData.sourceScorecardName);

      setConsolidationIndividualsMap((prevMap: EEScorecard.ConsolidationIndividualsMap) => {
        const updatedMatches: EEScorecard.ConsolidationMatch[] = filteredIndividualEmploymentRecords.map((employeeData) => ({
          sourceScorecardName: employeeData.sourceScorecardName,
          employeeDataId: employeeData.individualEmployeeDataId, 
        }));

        return {
          ...prevMap,
          [individual.individualId]: updatedMatches,
        };
      });
  };

  const conflictsExport = async () => {
    const parameters: ExportKeyValue[] = [
      {
        key: "ScorecardId",
        value: props.scorecardId.toString(),
      },
      {
        key: "ConsolidationId",
        value: props.consolidationId.toString(),
      },
      {
        key: "Sampled",
        value: "false",
      },
    ];

    const params: FileExportRequest = {
      Parameters: parameters,
      Type: "PeopleManagement",
    };
    setIsLoading(true);
    try {
      const file = await ImportService.ExportFile(params);
      var csvContent = "data:text/csv;charset=utf-8," + file.data;
      var encodedUri = encodeURI(csvContent);
      var link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute(
        "download",
        DataImportType[DataImportType.PeopleManagement] + ".csv"
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const CloseModal = async () => {
      props.closeConsolidationResultsModal();
      history.push(`/ee/scorecard/${props.scorecardId}/general`);
  };

  const ResolveConflicts = async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      const ConsolidationConflictsDto: EEScorecard.ConsolidationConflictsDto = {
        scorecardId: props.scorecardId,
        consolidationId: props.consolidationId,
        consolidationsIndividualMap: consolidationIndividualsMap,
      };
      await EEScorecardService.ResolveConflicts(ConsolidationConflictsDto);
      CloseModal();
      return true;
    } catch (error: any) {
      console.error(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const AutoResolveConflicts = async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      const ConsolidationConflictsDto: EEScorecard.ConsolidationConflictsDto = {
        scorecardId: props.scorecardId,
        consolidationId: props.consolidationId,
        consolidationsIndividualMap: consolidationIndividualsMap,
      };
      await EEScorecardService.AutoResolveConflicts(ConsolidationConflictsDto);
      CloseModal();  
      return true;
    } catch (error: any) {
      console.error(error);
      toast.error("Auto Consolidation failed");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const checkAllowUserToCloseModal = () => {
    const selectedEmployeeIds = Object.keys(consolidationIndividualsMap).length;
    if (totalIndividualConflicts === selectedEmployeeIds) {
      setShowValidationFooterMessage(false);
    } else {
      setShowValidationFooterMessage(true);
    }
  };

  useEffect(() => {
    checkAllowUserToCloseModal();
  }, [
    totalIndividualConflicts,
    Object.keys(consolidationIndividualsMap).length,
  ]);

  return (
    <Dialog
      disableEnforceFocus
      open={props.showConsolidationResultsModal}
      aria-labelledby="form-dialog-title"
      fullWidth={true}
      maxWidth="xl"
    >
      <DialogTitle>Resolve Employee Conflicts</DialogTitle>
      <DialogContent>
        {loading && <SkeletonLoading formLoading FieldAmount={6} />}
        {!loading && (
          <Box>
            <Grid container>
              <Grid item xs={10}>
                <MDTypography variant="h7">
                  Please be aware your consolidation completed with errors.
                  <span>{NumberOfIndividualsText()}</span>
                </MDTypography>
              </Grid>
              <Grid item xs={10}>
                <MDTypography variant="h7">
                  Please select the employee record applicable to the
                  consolidation scorecard
                </MDTypography>
              </Grid>
            </Grid>
            <div id="consolidationResult">
              {conflictData?.map(
                (
                  individual: EEScorecard.IndividualConsolidationDto,
                  index: number
                ) => (
                  <div key={`individualContainer${index}`}>
                    {true && (
                      <div key={`individualRow${index}`}>
                        <AppAccordion label={`${individual.idNumber} - ${individual.firstName} ${individual.lastName}`} expandOnDefault={true}>


                        <Grid container spacing={2} className="grid-row-header">
                          <Grid item xs={2}>
                            <span>Employment Status</span>
                          </Grid>
                          <Grid item xs={2}>
                            Occupation Level
                          </Grid>
                          <Grid item xs={3}>
                            Position
                          </Grid>
                          <Grid item xs={2}>
                            Start Date
                          </Grid>
                          <Grid item xs={1}>
                            End Date
                          </Grid>
                          <Grid item xs={2}>
                            Source
                          </Grid>
                        </Grid>

                        {individual.individualEmploymentRecords.map(
                          (
                            employmentData: EEScorecard.IndividualEmploymentRecordConsolidationDto
                          ) => (
                            <Grid
                              container
                              spacing={2}
                              className="grid-row"
                              key={employmentData.individualEmployeeDataId}
                            >
                              {
                                <>
                                  <Grid item xs={2}>
                                    <Radio
                                      name={individual.idNumber}
                                      id={
                                        individual.individualId +
                                        employmentData.sourceScorecardName
                                      }
                                      value={
                                        employmentData.individualEmployeeDataId
                                      }
                                      checked={
                                        consolidationIndividualsMap[individual.individualId]?.some(
                                        (match) => match.sourceScorecardName === employmentData.sourceScorecardName
                                      ) ?? false
                                      }
                                      onChange={(e) =>
                                        handleRadioChange(individual, employmentData)
                                      }
                                    />
                                    <label
                                      htmlFor={
                                        individual.idNumber +
                                        employmentData.individualEmployeeDataId
                                      }
                                    >
                                      {employmentData.employmentStatus}
                                    </label>
                                  </Grid>
                                  <Grid item xs={2}>
                                    <label
                                      htmlFor={
                                        individual.idNumber +
                                        employmentData.individualEmployeeDataId
                                      }
                                    >
                                      {employmentData.occupationLevel}
                                    </label>
                                  </Grid>
                                  <Grid item xs={3}>
                                    <label
                                      htmlFor={
                                        individual.idNumber +
                                        employmentData.individualEmployeeDataId
                                      }
                                    >
                                      {employmentData.position}
                                    </label>
                                  </Grid>
                                  <Grid item xs={2}>
                                    <label
                                      htmlFor={
                                        individual.idNumber +
                                        employmentData.individualEmployeeDataId
                                      }
                                    >
                                      {employmentData.startDateDateTime}
                                    </label>
                                  </Grid>
                                  <Grid item xs={1}>
                                    <label
                                      htmlFor={
                                        individual.idNumber +
                                        employmentData.individualEmployeeDataId
                                      }
                                    >
                                      {employmentData.endDateDateTime}
                                    </label>
                                  </Grid>
                                  <Grid item xs={2}>
                                    <label
                                      htmlFor={
                                        individual.idNumber +
                                        employmentData.individualEmployeeDataId
                                      }
                                    >
                                      {employmentData.sourceScorecardName}
                                    </label>
                                  </Grid>
                                </>
                              }
                            </Grid>
                          )
                        )}
                      </AppAccordion>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          </Box>
        )}
      </DialogContent>
        <Grid container>
          <Grid item xs="auto" sx={{ ml: 2 }}>
            <MDTypography variant="h7">
              Note that all employee records for the same scorecard as the record you select will be included in the
              consolidated scorecard.
            </MDTypography>
          </Grid>
        </Grid>
      <DialogActions>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <LoadingButton
                  loading={loading}
                  variant="contained"
                  type="button"
                  onClick={GoToPreviousPage}
                  color="primary"
                  disabled={!canGoToPreviousPage}
                >
                  Previous
                </LoadingButton>
              </Grid>
              <Grid item>
                <Typography variant="h6">
                  Page {pageNumber} of {totalPages}
                </Typography>
              </Grid>
              <Grid item>
                <LoadingButton
                  loading={loading}
                  variant="contained"
                  type="button"
                  onClick={GoToNextPage}
                  color="primary"
                  disabled={!canGoToNextPage}
                >
                  Next
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>

          <Grid item>
            <Box display="flex" justifyContent="space-between" width="100%">
              <LoadingButton
                loading={loading}
                variant="contained"
                type="button"
                onClick={conflictsExport}
                color="primary"
              >
                Export Conflicts
              </LoadingButton>
              <LoadingButton
                loading={loading}
                variant="contained"
                type="button"
                onClick={() => AutoResolveConflicts()} 
                color="primary"
              >
                Auto Resolve Conflicts
              </LoadingButton>
              <LoadingButton
                loading={loading}
                variant="contained"
                type="button"
                onClick={() => ResolveConflicts()}
                disabled={showValidationFooterMessage}
                color="primary"
              >
                Save
              </LoadingButton>
            </Box>
            <Grid item>
              {showValidationFooterMessage && (
                <Typography variant="h6" color={"red"}>
                  Please select the applicable employee records
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
};
